import { format } from "date-fns";
import { fi } from "date-fns/locale";

interface RouteItem {
  position: number;
  orderNumber: string;
  customerName: string;
  cemeteryName: string;
  cemeteryCity: string;
  taskType: string;
  lat: number;
  lng: number;
}

interface PrintableRouteListProps {
  loadDate: string;
  installerName: string;
  notes: string;
  routeItems: RouteItem[];
  totalDistanceKm?: number;
  estimatedDurationMin?: number;
  depotName: string;
}

export function PrintableRouteList({
  loadDate,
  installerName,
  notes,
  routeItems,
  totalDistanceKm,
  estimatedDurationMin,
  depotName,
}: PrintableRouteListProps) {
  return (
    <div className="p-8 bg-white text-black min-h-screen font-sans">
      {/* Header */}
      <div className="border-b-2 border-black pb-4 mb-6">
        <h1 className="text-2xl font-bold">Reittilista</h1>
        <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-semibold">Päivämäärä:</span>{" "}
            {format(new Date(loadDate), "EEEE d.M.yyyy", { locale: fi })}
          </div>
          <div>
            <span className="font-semibold">Asentaja:</span> {installerName}
          </div>
          {totalDistanceKm && (
            <div>
              <span className="font-semibold">Kokonaismatka:</span>{" "}
              {totalDistanceKm} km
            </div>
          )}
          {estimatedDurationMin && (
            <div>
              <span className="font-semibold">Arvioitu ajoaika:</span>{" "}
              {Math.floor(estimatedDurationMin / 60)}h {estimatedDurationMin % 60}min
            </div>
          )}
        </div>
        {notes && (
          <div className="mt-2 text-sm">
            <span className="font-semibold">Muistiinpanot:</span> {notes}
          </div>
        )}
      </div>

      {/* Starting point */}
      <div className="mb-4 p-3 bg-gray-100 rounded border">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-lg">
            🏠
          </span>
          <div>
            <div className="font-semibold">Lähtöpiste</div>
            <div className="text-sm text-gray-600">{depotName}</div>
          </div>
        </div>
      </div>

      {/* Route items */}
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-black">
            <th className="text-left py-2 w-12">#</th>
            <th className="text-left py-2">Tilaus</th>
            <th className="text-left py-2">Asiakas</th>
            <th className="text-left py-2">Hautausmaa</th>
            <th className="text-left py-2">Tehtävä</th>
            <th className="text-left py-2 w-20">Tehty</th>
          </tr>
        </thead>
        <tbody>
          {routeItems.map((item, index) => (
            <tr key={index} className="border-b border-gray-300">
              <td className="py-3">
                <span className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                  {item.position}
                </span>
              </td>
              <td className="py-3 font-medium">{item.orderNumber}</td>
              <td className="py-3">{item.customerName}</td>
              <td className="py-3">
                {item.cemeteryName}
                <div className="text-xs text-gray-500">{item.cemeteryCity}</div>
              </td>
              <td className="py-3">
                {item.taskType === "installation" ? "Asennus" : 
                 item.taskType === "delivery" ? "Toimitus" : "Nouto"}
              </td>
              <td className="py-3">
                <div className="w-6 h-6 border-2 border-black rounded"></div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Return point */}
      <div className="mt-4 p-3 bg-gray-100 rounded border">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-lg">
            🏠
          </span>
          <div>
            <div className="font-semibold">Paluupiste</div>
            <div className="text-sm text-gray-600">{depotName}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t text-xs text-gray-500">
        Tulostettu: {format(new Date(), "d.M.yyyy HH:mm")}
      </div>
    </div>
  );
}

export function openPrintableRouteList(props: PrintableRouteListProps) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("Ponnahdusikkuna estetty. Salli ponnahdusikkunat tulostaaksesi.");
    return;
  }

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Reittilista - ${format(new Date(props.loadDate), "d.M.yyyy")}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; }
        .container { padding: 24px; }
        .header { border-bottom: 2px solid black; padding-bottom: 16px; margin-bottom: 24px; }
        .header h1 { font-size: 24px; font-weight: bold; margin-bottom: 8px; }
        .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 14px; }
        .notes { margin-top: 8px; font-size: 14px; }
        .depot { margin-bottom: 16px; padding: 12px; background: #f3f4f6; border-radius: 4px; border: 1px solid #e5e7eb; display: flex; align-items: center; gap: 8px; }
        .depot-icon { width: 32px; height: 32px; border-radius: 50%; background: #7c3aed; color: white; display: flex; align-items: center; justify-content: center; font-size: 18px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { text-align: left; padding: 8px 4px; border-bottom: 2px solid black; font-weight: 600; }
        td { padding: 12px 4px; border-bottom: 1px solid #d1d5db; vertical-align: top; }
        .position { width: 28px; height: 28px; border-radius: 50%; background: #2563eb; color: white; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 14px; }
        .checkbox { width: 20px; height: 20px; border: 2px solid black; border-radius: 4px; }
        .city { font-size: 11px; color: #6b7280; }
        .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; }
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .container { padding: 0; }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Reittilista</h1>
          <div class="header-grid">
            <div><strong>Päivämäärä:</strong> ${format(new Date(props.loadDate), "EEEE d.M.yyyy", { locale: fi })}</div>
            <div><strong>Asentaja:</strong> ${props.installerName}</div>
            ${props.totalDistanceKm ? `<div><strong>Kokonaismatka:</strong> ${props.totalDistanceKm} km</div>` : ""}
            ${props.estimatedDurationMin ? `<div><strong>Arvioitu ajoaika:</strong> ${Math.floor(props.estimatedDurationMin / 60)}h ${props.estimatedDurationMin % 60}min</div>` : ""}
          </div>
          ${props.notes ? `<div class="notes"><strong>Muistiinpanot:</strong> ${props.notes}</div>` : ""}
        </div>

        <div class="depot">
          <div class="depot-icon">🏠</div>
          <div>
            <div style="font-weight: 600;">Lähtöpiste</div>
            <div style="font-size: 13px; color: #4b5563;">${props.depotName}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Tilaus</th>
              <th>Asiakas</th>
              <th>Hautausmaa</th>
              <th>Tehtävä</th>
              <th style="width: 60px;">Tehty</th>
            </tr>
          </thead>
          <tbody>
            ${props.routeItems.map((item) => `
              <tr>
                <td><div class="position">${item.position}</div></td>
                <td style="font-weight: 500;">${item.orderNumber}</td>
                <td>${item.customerName}</td>
                <td>
                  ${item.cemeteryName}
                  <div class="city">${item.cemeteryCity}</div>
                </td>
                <td>${item.taskType === "installation" ? "Asennus" : item.taskType === "delivery" ? "Toimitus" : "Nouto"}</td>
                <td><div class="checkbox"></div></td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <div class="depot" style="margin-top: 16px;">
          <div class="depot-icon">🏠</div>
          <div>
            <div style="font-weight: 600;">Paluupiste</div>
            <div style="font-size: 13px; color: #4b5563;">${props.depotName}</div>
          </div>
        </div>

        <div class="footer">
          Tulostettu: ${format(new Date(), "d.M.yyyy HH:mm")}
        </div>
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}
